import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";

const HERO_SLIDES = [
    {
        id: "beach",
        src: "https://plus.unsplash.com/premium_photo-1723708845453-9946cb95c8f8?w=1200&q=85&auto=format&fit=crop",
        label: "BEACH READY",
        sub: "/ swimwear · vacation /",
        bg: "#FF00FF",
    },
    {
        id: "getaway",
        src: "https://plus.unsplash.com/premium_photo-1670002286393-a08284e7b6ce?w=1200&q=85&auto=format&fit=crop",
        label: "GETAWAY",
        sub: "/ resort · summer /",
        bg: "#00E5FF",
    },
    {
        id: "office",
        src: "https://plus.unsplash.com/premium_photo-1661587832242-abbfc46a2e90?w=1200&q=85&auto=format&fit=crop",
        label: "POWER SUIT",
        sub: "/ business · tailored /",
        bg: "#CCFF00",
    },
    {
        id: "wedding",
        src: "https://images.unsplash.com/photo-1745270029066-8c1dfb37c03c?w=1200&q=85&auto=format&fit=crop",
        label: "WEDDING SZN",
        sub: "/ formal · groomsman /",
        bg: "#FF00FF",
    },
];

export const HeroSlideshow = () => {
    const [i, setI] = useState(0);
    const total = HERO_SLIDES.length;

    useEffect(() => {
        const t = setInterval(() => setI((p) => (p + 1) % total), 4500);
        return () => clearInterval(t);
    }, [total]);

    const next = () => setI((p) => (p + 1) % total);
    const prev = () => setI((p) => (p - 1 + total) % total);
    const slide = HERO_SLIDES[i];

    return (
        <div
            data-testid="hero-slideshow"
            className="border-4 border-black shadow-brutal-lg overflow-hidden relative h-full min-h-[300px] md:min-h-[360px]"
            style={{ backgroundColor: slide.bg, transition: "background-color 600ms" }}
        >
            <AnimatePresence mode="wait">
                <motion.img
                    key={slide.id}
                    src={slide.src}
                    alt={slide.label}
                    data-testid={`hero-slide-${slide.id}`}
                    initial={{ opacity: 0, scale: 1.04 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.98 }}
                    transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                    className="absolute inset-0 w-full h-full object-cover mix-blend-multiply"
                />
            </AnimatePresence>

            <div className="absolute top-3 left-3 z-10 bg-black text-[#CCFF00] border-2 border-black px-2 py-1 font-mono text-[10px] uppercase tracking-widest">
                {String(i + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
            </div>

            <AnimatePresence mode="wait">
                <motion.div
                    key={`cap-${slide.id}`}
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -10, opacity: 0 }}
                    transition={{ duration: 0.4, delay: 0.1 }}
                    className="absolute left-3 right-3 bottom-3 bg-white border-2 border-black px-3 py-2 z-10 flex items-center justify-between gap-2 shadow-brutal-sm"
                >
                    <div className="min-w-0">
                        <p className="font-display text-lg md:text-xl font-black uppercase tracking-tight leading-none truncate">
                            {slide.label}
                        </p>
                        <p className="font-mono text-[10px] uppercase tracking-widest text-black/60 mt-1 truncate">
                            {slide.sub}
                        </p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                        <button
                            type="button"
                            onClick={prev}
                            aria-label="Previous slide"
                            data-testid="hero-slide-prev"
                            className="bg-white border-2 border-black p-1 btn-press hover:bg-[#CCFF00]"
                        >
                            <ChevronLeft className="w-3.5 h-3.5" />
                        </button>
                        <button
                            type="button"
                            onClick={next}
                            aria-label="Next slide"
                            data-testid="hero-slide-next"
                            className="bg-white border-2 border-black p-1 btn-press hover:bg-[#CCFF00]"
                        >
                            <ChevronRight className="w-3.5 h-3.5" />
                        </button>
                    </div>
                </motion.div>
            </AnimatePresence>

            <div className="absolute top-3 right-3 z-10 flex gap-1.5">
                {HERO_SLIDES.map((s, idx) => (
                    <button
                        key={s.id}
                        type="button"
                        onClick={() => setI(idx)}
                        aria-label={`Go to slide ${idx + 1}`}
                        data-testid={`hero-slide-dot-${idx}`}
                        className={`w-3 h-3 border-2 border-black transition-all ${
                            idx === i ? "bg-[#CCFF00] w-6" : "bg-white"
                        }`}
                    />
                ))}
            </div>
        </div>
    );
};
